import { Auth } from 'aws-amplify';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { getBackendBaseUrl } from '@/api/backendAuth';
import { getAuthMode } from '@/utils/auth/authMode';
import { getMockBearerToken } from '@/utils/auth/mockAuthSession';
import { getSessionBearerToken } from '@/utils/auth/sessionTokenBridge';

/**
 * Ensures calls to the local .NET API use a current session JWT (Clerk or Cognito).
 * Does not override SDK composite keys (Bearer cs_...).
 */

function isPooledTenantApiUrl(url: string | undefined): boolean {
  if (!url) return false;
  try {
    const base = getBackendBaseUrl();
    return url.startsWith(base);
  } catch {
    return false;
  }
}

/** Registration endpoints are AllowAnonymous; attaching a Clerk JWT triggers tenant auto-provision. */
function isRegistrationApiUrl(url: string | undefined): boolean {
  if (!url || !isPooledTenantApiUrl(url)) return false;
  try {
    const path = new URL(url, 'http://local.invalid').pathname;
    return path === '/registration' || path.startsWith('/registration/');
  } catch {
    return /\/registration(\/|$)/i.test(url);
  }
}

function shouldAttachSessionToken(config: InternalAxiosRequestConfig): boolean {
  if (!isPooledTenantApiUrl(config.url)) return false;
  if (isRegistrationApiUrl(config.url)) return false;
  const auth = String(config.headers?.Authorization ?? '');
  if (auth.includes('Bearer cs_')) return false;
  return true;
}

async function resolveSessionBearerToken(): Promise<string | null> {
  const mode = getAuthMode();
  if (mode === 'clerk') {
    return getSessionBearerToken();
  }
  if (mode === 'aws-cognito') {
    try {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    } catch {
      return null;
    }
  }
  if (mode === 'local-mock-jwt') {
    const token = getMockBearerToken();
    return token || null;
  }
  return null;
}

axios.interceptors.request.use(async (config) => {
  if (!shouldAttachSessionToken(config)) return config;
  const token = await resolveSessionBearerToken();
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

type RetriableConfig = InternalAxiosRequestConfig & { _sessionAuthRetry?: boolean };

axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    if (!original || error.response?.status !== 401 || original._sessionAuthRetry) {
      return Promise.reject(error);
    }
    if (!isPooledTenantApiUrl(original.url)) {
      return Promise.reject(error);
    }
    const auth = String(original.headers?.Authorization ?? '');
    if (auth.includes('Bearer cs_')) {
      return Promise.reject(error);
    }
    const token = await resolveSessionBearerToken();
    if (!token) {
      return Promise.reject(error);
    }
    original._sessionAuthRetry = true;
    original.headers = original.headers ?? {};
    (original.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    return axios(original);
  },
);
